import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from "react";
import { dropIndex, moveItem, reorderOffsets, type ReorderMetrics } from "@/lib/reorder";

/**
 * Drag a row — the whole group it heads — to a new place in a list.
 *
 * There is no handle: the row itself is the grip, so the gesture has to tell a
 * drag from the click and the scroll that share it. A mouse commits after 4px
 * of travel; a finger has to hold first, because on touch the same downward
 * motion is how you scroll the list.
 *
 * Nothing here re-renders. The drag measures the list once and then writes
 * `transform` on the elements it moves inside one rAF loop; React is not told
 * about a drag at all until the order it produced is committed, so a long
 * project tree is never re-rendered mid-gesture. The rows that make way
 * carry a CSS transition, so they glide to their new slots while the dragged
 * row tracks the pointer exactly.
 */

/** Past this from an edge, a live drag scrolls the list under itself. */
const EDGE_SCROLL_ZONE = 44;
const EDGE_SCROLL_MAX = 14;
const POINTER_THRESHOLD = 4;
const TOUCH_HOLD_MS = 320;
const TOUCH_HOLD_SLOP = 10;
const SETTLE_MS = 170;
const SETTLE_EASING = "cubic-bezier(0.22, 0.61, 0.36, 1)";

interface DragSession {
  id: string;
  pointerId: number;
  startY: number;
  startScrollTop: number;
  pointerY: number;
  active: boolean;
  holdTimer: ReturnType<typeof setTimeout> | null;
  frame: number | null;
  container: HTMLElement;
  elements: HTMLElement[];
  ids: string[];
  metrics: ReorderMetrics[];
  gap: number;
  from: number;
  to: number;
  /** Where the lifted row is right now, so the release knows what to ease from. */
  delta: number;
}

export interface DragReorderHandle {
  /** Attach to the row that acts as the grip. */
  onPointerDown: (event: ReactPointerEvent<HTMLElement>, id: string) => void;
}

export function useDragReorder({ ids, onCommit }: { ids: string[]; onCommit: (ids: string[]) => void }): DragReorderHandle {
  const session = useRef<DragSession | null>(null);
  // Read through refs so the window listeners are installed once per drag and
  // never go stale as the sidebar re-renders underneath one.
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const cleanup = useCallback((commitOrder?: string[]) => {
    const drag = session.current;
    session.current = null;
    if (!drag) return;
    if (drag.holdTimer) clearTimeout(drag.holdTimer);
    if (drag.frame !== null) cancelAnimationFrame(drag.frame);
    for (const element of drag.elements) {
      element.style.transform = "";
      element.style.transition = "";
      element.style.willChange = "";
      element.style.zIndex = "";
      element.classList.remove("reorder-dragging");
    }
    delete drag.container.dataset.reordering;
    // The commit lands in the same frame the transforms are dropped, so the
    // rows never flash back through their old positions on the way to the
    // order they are already sitting in.
    if (commitOrder) commitRef.current(commitOrder);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const paint = useCallback(() => {
    const drag = session.current;
    if (!drag || !drag.active) return;
    const delta = (drag.pointerY - drag.startY) + (drag.container.scrollTop - drag.startScrollTop);
    const to = dropIndex(drag.metrics, drag.from, delta, drag.gap);
    if (to !== drag.to) {
      drag.to = to;
      const offsets = reorderOffsets(drag.metrics, drag.from, to, drag.gap);
      drag.elements.forEach((element, index) => {
        if (index === drag.from) return;
        element.style.transform = offsets[index] ? `translate3d(0, ${offsets[index]}px, 0)` : "";
      });
    }
    drag.delta = delta;
    drag.elements[drag.from]!.style.transform = `translate3d(0, ${delta}px, 0)`;
  }, []);

  const tick = useCallback(() => {
    const drag = session.current;
    if (!drag || !drag.active) return;
    drag.frame = requestAnimationFrame(tick);
    // Edge auto-scroll: the pointer is standing still near the edge, so the
    // list has to come to it. Reading the bounds each frame keeps a resized or
    // scrolled pane honest, and both reads are cheap next to the paint.
    const bounds = drag.container.getBoundingClientRect();
    const above = bounds.top + EDGE_SCROLL_ZONE - drag.pointerY;
    const below = drag.pointerY - (bounds.bottom - EDGE_SCROLL_ZONE);
    const speed = above > 0 ? -Math.min(EDGE_SCROLL_MAX, (above / EDGE_SCROLL_ZONE) * EDGE_SCROLL_MAX)
      : below > 0 ? Math.min(EDGE_SCROLL_MAX, (below / EDGE_SCROLL_ZONE) * EDGE_SCROLL_MAX)
      : 0;
    if (speed) drag.container.scrollTop += speed;
    paint();
  }, [paint]);

  const activate = useCallback(() => {
    const drag = session.current;
    if (!drag || drag.active) return;
    const container = drag.container;
    const elements = Array.from(container.querySelectorAll<HTMLElement>(":scope > [data-reorder-item]"));
    const from = elements.findIndex((element) => element.dataset.reorderId === drag.id);
    if (from < 0 || elements.length < 2) { cleanup(); return; }
    // `offsetTop` is layout position, not viewport position: it survives the
    // list scrolling under the drag, which is exactly what auto-scroll does.
    drag.elements = elements;
    drag.ids = elements.map((element) => element.dataset.reorderId ?? "");
    drag.metrics = elements.map((element) => ({ top: element.offsetTop, height: element.offsetHeight }));
    drag.gap = elements.length > 1
      ? Math.max(0, drag.metrics[1]!.top - (drag.metrics[0]!.top + drag.metrics[0]!.height))
      : 0;
    drag.from = from;
    drag.to = from;
    drag.active = true;
    container.dataset.reordering = "true";
    const lifted = elements[from]!;
    lifted.classList.add("reorder-dragging");
    lifted.style.willChange = "transform";
    lifted.style.zIndex = "3";
    paint();
    drag.frame = requestAnimationFrame(tick);
  }, [cleanup, paint, tick]);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const drag = session.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      drag.pointerY = event.clientY;
      if (drag.active) {
        // The row is following the finger now, so the list must not also
        // scroll under it.
        if (event.cancelable) event.preventDefault();
        paint();
        return;
      }
      if (drag.holdTimer) {
        // Still waiting on a press-and-hold: any real travel is a scroll.
        if (Math.abs(event.clientY - drag.startY) > TOUCH_HOLD_SLOP) cleanup();
        return;
      }
      if (Math.abs(event.clientY - drag.startY) >= POINTER_THRESHOLD) activate();
    };

    const onTouchMove = (event: TouchEvent) => {
      if (session.current?.active && event.cancelable) event.preventDefault();
    };

    const onUp = (event: PointerEvent) => {
      const drag = session.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (!drag.active) { cleanup(); return; }

      // A drag is not a click. The row is a button (it expands the project),
      // so the click the release is about to raise has to be swallowed once.
      // Removed on a timer as well as on the first click, because a release
      // that lands outside the row raises no click at all and the listener
      // would otherwise wait to eat someone else's.
      const swallow = (click: MouseEvent) => { click.preventDefault(); click.stopPropagation(); };
      window.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 300);

      const { from, to, metrics, gap, elements, ids: order } = drag;
      const settled = moveItem(order, from, to);
      const offsets = reorderOffsets(metrics, from, to, gap);
      const lifted = elements[from]!;
      const target = offsets[from] ?? 0;
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      const current = drag.delta;
      if (drag.frame !== null) { cancelAnimationFrame(drag.frame); drag.frame = null; }
      drag.active = false;

      if (reduced || Math.abs(current - target) < 1) {
        cleanup(from === to ? undefined : settled);
        return;
      }
      // Drop, don't teleport: the lifted row runs to the slot the others have
      // already opened for it, and the order is committed as it arrives.
      lifted.style.transition = `transform ${SETTLE_MS}ms ${SETTLE_EASING}`;
      lifted.style.transform = target ? `translate3d(0, ${target}px, 0)` : "translate3d(0, 0, 0)";
      setTimeout(() => cleanup(from === to ? undefined : settled), SETTLE_MS);
    };

    const onCancel = () => cleanup();
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [activate, cleanup, paint]);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>, id: string) => {
    if (event.button !== 0 || session.current || idsRef.current.length < 2) return;
    const target = event.target as HTMLElement | null;
    // The row's own controls (the add button) keep their clicks.
    if (target?.closest("[data-no-drag]")) return;
    const element = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-reorder-item]");
    const container = element?.parentElement;
    if (!element || !container) return;

    session.current = {
      id,
      pointerId: event.pointerId,
      startY: event.clientY,
      startScrollTop: container.scrollTop,
      pointerY: event.clientY,
      active: false,
      holdTimer: null,
      frame: null,
      container,
      elements: [],
      ids: [],
      metrics: [],
      gap: 0,
      from: -1,
      to: -1,
      delta: 0,
    };
    // Touch and pen scroll the list with the same stroke, so they ask for the
    // familiar press-and-hold before the row comes loose.
    if (event.pointerType !== "mouse") {
      session.current.holdTimer = setTimeout(() => {
        if (!session.current) return;
        session.current.holdTimer = null;
        activate();
      }, TOUCH_HOLD_MS);
    }
  }, [activate]);

  return { onPointerDown };
}
